import { useEffect, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { QuoteAttachmentPreviewDialog } from '@/components/QuoteAttachmentPreviewDialog';
import { DialogCloseButton } from '@/components/misc/DialogCloseButton';
import { useDataProvider } from 'react-admin';
import type { CrmDataProvider } from '@/providers/types';
import { formatDate } from '@/utils/date_utils';
import {
    defaultTypeOfWork,
    roofType,
    roofingSystem,
} from '@/root/defaultConfiguration';
import {
    QuoteAttachment,
    relatedQuoteFilesFromEstimationData,
    viewFilesFromEstimationData,
} from '@/utils/quote_attachment_utils';
import {
    type EstimatorProjectByAddress,
    type EstimatorSiteHistoryQuote,
    isRelatedOrViewsSection,
    projectFileToHistoryAttachment,
    resolveSiteHistoryAttachments,
} from './siteHistoryUtils';

export type {
    EstimatorProjectByAddress,
    EstimatorSiteHistoryFile,
    EstimatorSiteHistoryQuote,
} from './siteHistoryUtils';
export {
    aggregateSiteHistoryQuotes,
    isLegacyOnlyProject,
    selectableProjectsByAddress,
} from './siteHistoryUtils';

function formatUsd(amount: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(amount);
}

function formatSf(area: number): string {
    return `${Math.round(area).toLocaleString('en-US')} SF`;
}

function displayTypeOfWork(value: string | null | undefined): string | null {
    if (!value) return null;
    return defaultTypeOfWork[value] ?? value;
}

function displayRoofType(value: string | null | undefined): string | null {
    if (!value) return null;
    return roofType[value] ?? value;
}

function displayRoofSystem(
    roofTypeKey: string | null | undefined,
    systemKey: string | null | undefined,
): string | null {
    if (!roofTypeKey || !systemKey) return null;
    return roofingSystem[roofTypeKey]?.[systemKey] ?? systemKey;
}

interface SiteHistoryPanelProps {
    quotes: EstimatorSiteHistoryQuote[];
    projectsByAddress?: EstimatorProjectByAddress[];
    onCopyClient?: (row: EstimatorProjectByAddress) => void;
}

function projectForQuote(
    quote: EstimatorSiteHistoryQuote,
    projects: EstimatorProjectByAddress[],
): EstimatorProjectByAddress | null {
    if (quote.project_id == null) return null;
    return projects.find(p => p.id === quote.project_id) ?? null;
}

function projectForLegacyCopy(
    quote: EstimatorSiteHistoryQuote,
    projects: EstimatorProjectByAddress[],
): EstimatorProjectByAddress | null {
    return projectForQuote(quote, projects) ?? projects[0] ?? null;
}

export function SiteHistoryPanel({
    quotes,
    projectsByAddress = [],
    onCopyClient,
}: SiteHistoryPanelProps) {
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [expanded, setExpanded] = useState(true);
    const [selectedQuote, setSelectedQuote] = useState<EstimatorSiteHistoryQuote | null>(null);
    const [quoteAttachments, setQuoteAttachments] = useState<QuoteAttachment[]>([]);
    const [attachmentsLoading, setAttachmentsLoading] = useState(false);
    const [preview, setPreview] = useState<QuoteAttachment | null>(null);

    useEffect(() => {
        if (!selectedQuote) {
            setQuoteAttachments([]);
            setAttachmentsLoading(false);
            return undefined;
        }

        let cancelled = false;
        setAttachmentsLoading(true);
        setQuoteAttachments([]);

        dataProvider
            .getOne('quotes', { id: selectedQuote.id })
            .then(({ data }: { data: Record<string, unknown> }) => {
                if (cancelled) return;
                const estimationData =
                    (data as { estimation_data?: Record<string, unknown> | null })
                        .estimation_data ?? undefined;
                const related = relatedQuoteFilesFromEstimationData(estimationData);
                const views = viewFilesFromEstimationData(estimationData);
                setQuoteAttachments(
                    resolveSiteHistoryAttachments(
                        selectedQuote.id,
                        selectedQuote.files ?? [],
                        [...related, ...views],
                    ),
                );
            })
            .catch(() => {
                if (!cancelled) {
                    setQuoteAttachments(
                        resolveSiteHistoryAttachments(
                            selectedQuote.id,
                            selectedQuote.files ?? [],
                            [],
                        ),
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setAttachmentsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [selectedQuote, dataProvider]);

    if (quotes.length === 0) return null;

    const listedIds = new Set(
        quoteAttachments
            .map(file => file.project_file_id)
            .filter((id): id is number => id != null),
    );
    const projectFiles = (selectedQuote?.files ?? []).filter(
        file => !isRelatedOrViewsSection(file.section) && !listedIds.has(file.id),
    );
    const selectedProject = selectedQuote
        ? projectForQuote(selectedQuote, projectsByAddress)
        : null;
    const selectedContractorName = !selectedProject
        ? '—'
        : selectedProject.contractor_id == null
            || selectedProject.property_owner_id == null
            || Number(selectedProject.contractor_id) === Number(selectedProject.property_owner_id)
            ? selectedProject.client_name?.trim() || '—'
            : selectedProject.contractor_name?.trim() || '—';

    return (
        <Paper variant="outlined" sx={{ p: 1, mt: 1 }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: expanded ? 1 : 0 }}
            >
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Address history ({quotes.length})
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => setExpanded(v => !v)}
                        sx={{ textTransform: 'none', fontSize: 11, minWidth: 0 }}
                    >
                        {expanded ? 'Hide history' : 'Show history'}
                    </Button>
                </Stack>
            </Stack>

            {expanded && (
                <TableContainer sx={{ maxHeight: 160, overflowX: 'auto', paddingBottom: '15px' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontSize: 11, py: 0.5, width: 32, px: 0.5 }} />
                                <TableCell sx={{ fontSize: 11, py: 0.5 }}>No.</TableCell>
                                <TableCell sx={{ fontSize: 11, py: 0.5 }}>Project</TableCell>
                                <TableCell sx={{ fontSize: 11, py: 0.5 }}>Date</TableCell>
                                <TableCell sx={{ fontSize: 11, py: 0.5 }} align="right">Area</TableCell>
                                <TableCell sx={{ fontSize: 11, py: 0.5 }} align="right">Total</TableCell>
                                <TableCell sx={{ fontSize: 11, py: 0.5 }}>Work</TableCell>
                                <TableCell sx={{ fontSize: 11, py: 0.5 }}>Roof</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {quotes.map(quote => {
                                const copyProject =
                                    quote.is_legacy && onCopyClient
                                        ? projectForLegacyCopy(quote, projectsByAddress)
                                        : null;
                                return (
                                <TableRow
                                    key={quote.id}
                                    hover
                                    onClick={() => setSelectedQuote(quote)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell
                                        sx={{ fontSize: 11, py: 0.5, width: 32, px: 0.5 }}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        {copyProject && (
                                            <IconButton
                                                size="small"
                                                title="Copy client only"
                                                aria-label="Copy client only"
                                                onClick={() => onCopyClient?.(copyProject)}
                                                sx={{ p: 0.25 }}
                                            >
                                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <span>#{quote.folder_number ?? quote.id}</span>
                                            {quote.is_legacy && (
                                                <Chip
                                                    label="Legacy"
                                                    size="small"
                                                    sx={{ height: 18, fontSize: 10 }}
                                                />
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                                        {quote.project_phase_label ?? quote.project_phase ?? '—'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                                        {formatDate(quote.created_at, 'MM-dd-yyyy')}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 11, py: 0.5 }} align="right">
                                        {formatSf(quote.total_area)}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 11, py: 0.5 }} align="right">
                                        {formatUsd(quote.total_price)}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                                        {displayTypeOfWork(quote.type_of_work) ?? '—'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 11, py: 0.5 }}>
                                        {displayRoofType(quote.roof_type) ?? '—'}
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog
                open={!!selectedQuote}
                onClose={() => setSelectedQuote(null)}
                maxWidth="sm"
                fullWidth
            >
                {selectedQuote && (
                    <>
                        <DialogTitle sx={{ pr: 6 }}>
                            Quote #{selectedQuote.folder_number ?? selectedQuote.id}
                            {selectedQuote.is_legacy ? ' (Legacy)' : ''}
                            <DialogCloseButton onClose={() => setSelectedQuote(null)} />
                        </DialogTitle>
                        <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'flex-start' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() =>
                                    window.open(
                                        `/#/quotes/${selectedQuote.id}/show`,
                                        '_blank',
                                        'noopener,noreferrer',
                                    )
                                }
                                sx={{ textTransform: 'none' }}
                            >
                                Open quote view
                            </Button>
                        </DialogActions>
                        <DialogContent dividers>
                            <Stack spacing={1.25} sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Project:</strong>{' '}
                                    {selectedQuote.project_phase_label
                                        ?? selectedQuote.project_phase
                                        ?? '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Folio:</strong>{' '}
                                    {selectedProject?.folio_number || '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Owner:</strong>{' '}
                                    {selectedProject?.client_name?.trim() || '—'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Contractor:</strong> {selectedContractorName}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Status:</strong> {selectedQuote.status_label}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Date:</strong>{' '}
                                    {formatDate(selectedQuote.created_at, 'MM-dd-yyyy HH:mm')}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Area:</strong> {formatSf(selectedQuote.total_area)}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Total:</strong> {formatUsd(selectedQuote.total_price)}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Type of work:</strong>{' '}
                                    {displayTypeOfWork(selectedQuote.type_of_work) ?? 'Not selected'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Roof type:</strong>{' '}
                                    {displayRoofType(selectedQuote.roof_type) ?? 'Not selected'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Desired steep slope:</strong>{' '}
                                    {displayRoofSystem(
                                        selectedQuote.roof_type,
                                        selectedQuote.desired_steep_slope,
                                    ) ?? 'Not selected'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Desired low slope:</strong>{' '}
                                    {displayRoofSystem(
                                        selectedQuote.roof_type,
                                        selectedQuote.desired_low_slope,
                                    ) ?? 'Not selected'}
                                </Typography>
                            </Stack>

                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Files
                            </Typography>
                            <Divider sx={{ mb: 1 }} />

                            {attachmentsLoading && quoteAttachments.length === 0 && projectFiles.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    Loading files…
                                </Typography>
                            ) : quoteAttachments.length === 0 && projectFiles.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No files for this quote.
                                </Typography>
                            ) : (
                                <List dense disablePadding>
                                    {quoteAttachments.map(file => (
                                        <ListItem key={file.id} disablePadding>
                                            <ListItemButton onClick={() => setPreview(file)}>
                                                <ListItemText
                                                    primary={file.name || file.filename}
                                                    secondary={file.filename}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                    {projectFiles.map(file => (
                                        <ListItem key={`pf-${file.id}`} disablePadding>
                                            <ListItemButton
                                                onClick={() =>
                                                    setPreview(
                                                        projectFileToHistoryAttachment(
                                                            file,
                                                            selectedQuote.id,
                                                        ),
                                                    )
                                                }
                                            >
                                                <ListItemText
                                                    primary={file.title}
                                                    secondary={file.filename || file.title}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </DialogContent>
                    </>
                )}
            </Dialog>

            <QuoteAttachmentPreviewDialog
                attachment={preview}
                onClose={() => setPreview(null)}
            />
        </Paper>
    );
}
